import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { ArrowLeft, TrendingUp } from 'lucide-react';
import api from '../../services/api';

// Self-contained dark "panel" — intentionally NOT inside the admin sidebar chrome,
// so it reads as a clean investor view and stays out of the (full) admin tab nav.
const BG = '#070708';
const CARD = '#101013';
const BORDER = 'rgba(255,255,255,0.07)';
const MUTE = 'rgba(255,255,255,0.45)';
const FAINT = 'rgba(255,255,255,0.25)';
const BRAND = '#7C5CFF';

const money = (n) => '$' + Math.round(Number(n) || 0).toLocaleString();
const num = (n) => (Number(n) || 0).toLocaleString();
const pct = (n) => (Number(n) || 0).toFixed(1) + '%';

// Dev-only sample so the layout can be previewed before the server endpoint ships.
// Gated to NODE_ENV==='development' below — never reaches a production build.
const SAMPLE = {
  period_days: 90,
  gmv: { total: 342850, bottle: 214300, restaurant: 98200, ticket: 30350 },
  commission: { total: 53140, blended_rate: 15.5 },
  aov: 686,
  transactions: { total: 500, bottle: 280, restaurant: 140, ticket: 80 },
  revenue_over_time: [
    { date: '2026-04-05', gmv: 18200 }, { date: '2026-04-15', gmv: 21400 },
    { date: '2026-04-25', gmv: 19800 }, { date: '2026-05-05', gmv: 26500 },
    { date: '2026-05-15', gmv: 31200 }, { date: '2026-05-25', gmv: 29800 },
    { date: '2026-06-05', gmv: 38400 }, { date: '2026-06-15', gmv: 44100 },
    { date: '2026-06-25', gmv: 52300 },
  ],
  top_venues: [
    { venue_id: '1', venue_name: 'Mila', gmv: 84200, transactions: 96 },
    { venue_id: '2', venue_name: 'Casa Neos', gmv: 61500, transactions: 71 },
    { venue_id: '3', venue_name: 'M2', gmv: 48900, transactions: 64 },
    { venue_id: '4', venue_name: 'Bâoli', gmv: 39400, transactions: 52 },
    { venue_id: '5', venue_name: 'Sexy Fish', gmv: 28300, transactions: 33 },
  ],
  users: { total: 152000, new_in_period: 8400 },
};

const InvestorDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);
  const [isSample, setIsSample] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/analytics/investor-overview?days=${days}`);
      setData(res.data.data);
      setIsSample(false);
    } catch (e) {
      console.error('investor-overview error', e);
      if (process.env.NODE_ENV === 'development') { setData(SAMPLE); setIsSample(true); }
      else { setData(null); }
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const gmv = data?.gmv || {};
  const commission = data?.commission || {};
  const tx = data?.transactions || {};
  const series = data?.revenue_over_time || [];
  const topVenues = data?.top_venues || [];
  const users = data?.users || {};
  const fmtDate = (s) => { const d = new Date(s + 'T00:00:00'); return `${d.getMonth() + 1}/${d.getDate()}`; };

  const kpis = [
    { label: 'Gross Merchandise Value', value: money(gmv.total), sub: 'Total booked through ZONA' },
    { label: 'ZONA Revenue', value: money(commission.total), sub: `${pct(commission.blended_rate)} blended take rate` },
    { label: 'Transactions', value: num(tx.total), sub: 'Paid bookings' },
    { label: 'Avg Order Value', value: money(data?.aov), sub: 'Per transaction' },
  ];

  const streams = [
    { label: 'Bottle Service', g: gmv.bottle, t: tx.bottle },
    { label: 'Dinner', g: gmv.restaurant, t: tx.restaurant },
    { label: 'Tickets', g: gmv.ticket, t: tx.ticket },
  ];

  return (
    <div style={{ minHeight: '100vh', background: BG, color: '#fff', fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 28px', borderBottom: `1px solid ${BORDER}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: `1px solid ${BORDER}`, color: MUTE, borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 12 }}>
            <ArrowLeft size={14} /> Admin
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <TrendingUp size={18} color={BRAND} />
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: 0.3 }}>ZONA · Investor Overview</h1>
            </div>
            <p style={{ margin: '4px 0 0', color: MUTE, fontSize: 13 }}>Real-time performance across the marketplace</p>
            {isSample && <span style={{ display: 'inline-block', marginTop: 6, fontSize: 11, fontWeight: 600, color: '#F1C40F', background: 'rgba(241,196,15,0.1)', border: '1px solid rgba(241,196,15,0.25)', borderRadius: 100, padding: '2px 8px' }}>Preview · sample data (endpoint not deployed yet)</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[{ l: '30d', v: 30 }, { l: '90d', v: 90 }, { l: '1y', v: 365 }, { l: 'All', v: 3650 }].map((p) => (
            <button key={p.v} onClick={() => setDays(p.v)} style={{ background: days === p.v ? BRAND : 'transparent', color: days === p.v ? '#fff' : MUTE, border: `1px solid ${days === p.v ? BRAND : BORDER}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>{p.l}</button>
          ))}
        </div>
      </div>

      <div style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
        {loading ? (
          <div style={{ color: FAINT, padding: 80, textAlign: 'center' }}>Loading…</div>
        ) : !data ? (
          <div style={{ color: FAINT, padding: 80, textAlign: 'center' }}>
            No data available yet.
          </div>
        ) : (
          <>
            {/* KPI cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 16 }}>
              {kpis.map((k) => (
                <div key={k.label} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: '18px' }}>
                  <div style={{ color: MUTE, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{k.label}</div>
                  <div style={{ fontSize: 30, fontWeight: 800, margin: '8px 0 4px', letterSpacing: -0.5 }}>{k.value}</div>
                  <div style={{ color: FAINT, fontSize: 12 }}>{k.sub}</div>
                </div>
              ))}
            </div>

            {/* GMV over time */}
            <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: '18px', marginBottom: 16 }}>
              <h2 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700 }}>GMV over time</h2>
              {series.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={series}>
                    <defs>
                      <linearGradient id="gmvFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={BRAND} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={BRAND} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="date" tickFormatter={fmtDate} stroke={FAINT} fontSize={11} />
                    <YAxis stroke={FAINT} fontSize={11} tickFormatter={(v) => '$' + (v >= 1000 ? (v / 1000) + 'k' : v)} />
                    <Tooltip contentStyle={{ background: '#111', border: '1px solid #1a1a1a', borderRadius: 10, fontSize: 13 }} formatter={(v) => money(v)} labelFormatter={fmtDate} />
                    <Area type="monotone" dataKey="gmv" stroke={BRAND} strokeWidth={2.5} fill="url(#gmvFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <div style={{ color: FAINT, padding: 60, textAlign: 'center' }}>No transactions in this period yet</div>}
            </div>

            {/* streams + top venues */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: '18px' }}>
                <h2 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700 }}>GMV by stream</h2>
                {streams.map((s) => {
                  const share = gmv.total ? Math.round((Number(s.g || 0) / Number(gmv.total)) * 100) : 0;
                  return (
                    <div key={s.label} style={{ marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                        <span>{s.label} <span style={{ color: FAINT }}>· {num(s.t)} tx</span></span>
                        <span style={{ fontWeight: 700 }}>{money(s.g)}</span>
                      </div>
                      <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 100 }}>
                        <div style={{ height: 6, width: `${share}%`, background: BRAND, borderRadius: 100 }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: '18px' }}>
                <h2 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700 }}>Top venues by revenue</h2>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ color: MUTE, textAlign: 'left' }}>
                      <th style={{ padding: '4px 0', fontWeight: 600 }}>Venue</th>
                      <th style={{ padding: '4px 0', fontWeight: 600, textAlign: 'right' }}>GMV</th>
                      <th style={{ padding: '4px 0', fontWeight: 600, textAlign: 'right' }}>Tx</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topVenues.map((v, i) => (
                      <tr key={v.venue_id || i} style={{ borderTop: `1px solid ${BORDER}` }}>
                        <td style={{ padding: '8px 0' }}>{v.venue_name || '—'}</td>
                        <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 700 }}>{money(v.gmv)}</td>
                        <td style={{ padding: '8px 0', textAlign: 'right', color: MUTE }}>{num(v.transactions)}</td>
                      </tr>
                    ))}
                    {topVenues.length === 0 && <tr><td colSpan={3} style={{ padding: 24, textAlign: 'center', color: FAINT }}>No venue revenue yet</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {/* users footer */}
            <div style={{ display: 'flex', gap: 24, marginTop: 16, color: MUTE, fontSize: 13 }}>
              <span><strong style={{ color: '#fff' }}>{num(users.total)}</strong> total users</span>
              <span><strong style={{ color: '#fff' }}>{num(users.new_in_period)}</strong> new this period</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InvestorDashboard;
